// ============================================================
// SpringBootJpaRepo.jsx
//
// A branded, responsive course content page for the
// "JPA Repositories in Spring Boot" lesson.
//
// Author: ProDev Enterprise Series (Java Branch)
// Features: Repository hierarchy, Query Method derivation,
//           and advanced @Query patterns.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import StorageIcon            from "@mui/icons-material/Storage";
import FindInPageIcon          from "@mui/icons-material/FindInPage";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import SchemaIcon             from "@mui/icons-material/Schema";
import DatasetIcon            from "@mui/icons-material/Dataset";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function SpringBootJpaRepo() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Java Enterprise · Module 09"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        JPA <span>Repositories</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Abstract your persistence layer using Spring Data JPA. Repositories provide a high-level interface for database interactions, eliminating boilerplate DAO code and offering powerful query generation features.
                    </Typography>
                </Box>

                {/* 1. HIERARCHY */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SchemaIcon />} label="1. Repository Hierarchy" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Spring Data provides several base interfaces. While <code style={{color: BRAND.white}}>CrudRepository</code> handles basic operations, <strong>JpaRepository</strong> adds support for pagination, sorting, and batch flushing.
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <CodeBlock
                                title="Repository Interfaces"
                                code={`public interface UserRepo extends CrudRepository<User, Long> { }\n\n// Adds Paging, Sorting, and Batch Delete\npublic interface OrderRepo extends JpaRepository<Order, Long> { }`}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 1, fontSize: 14 }}>Why JpaRepository?</Typography>
                                <Typography sx={{ color: BRAND.muted, fontSize: 12, lineHeight: 1.6 }}>
                                    It contains the full API of CrudRepository and PagingAndSortingRepository. Use it when you need advanced features like <strong>flushing the persistence context</strong> or deleting records in a batch.
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                {/* 2. QUERY METHODS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<FindInPageIcon />} label="2. Query Method Derivation" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Simply by following a naming convention, Spring will automatically generate the SQL query for you. It parses the method name to determine the query criteria.
                    </Typography>
                    <CodeBlock
                        title="Convention-Based Queries"
                        code={`List<User> findByEmail(String email);\n\n// Translates to: SELECT * FROM products p WHERE p.category = ? AND p.price < ?\nList<Product> findByCategoryAndPriceLessThan(String category, double price);`}
                    />
                </Box>

                {/* 3. CUSTOM QUERIES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="3. @Query & Native SQL" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        When conventions aren't enough, use <code style={{color: BRAND.white}}>@Query</code> for JPQL (Object-oriented) or Native SQL (Database-specific) statements.
                    </Typography>
                    <CodeBlock
                        title="Manual Query Definition"
                        code={`@Query("SELECT u FROM User u WHERE u.name LIKE %:keyword%")\nList<User> searchByName(@Param("keyword") String keyword);\n\n@Query(value = "SELECT * FROM orders WHERE status = ?1", nativeQuery = true)\nList<Order> findByStatus(String status);`}
                    />
                </Box>

                {/* 4. DATA TYPES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<DatasetIcon />} label="4. Return Types & Pagination" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Repositories support various return types to handle null-safety and large datasets efficiently.
                    </Typography>
                    <CodeBlock
                        title="Repository Return Patterns"
                        code={`Optional<User> findById(Long id); // Null-safe single result\n\n// Returns metadata like totalPages and totalElements\nPage<User> findByCity(String city, Pageable pageable);`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Implementation Best Practices" />
                    <Grid container spacing={1.5}>
                        {[
                            "Prefer JpaRepository for enterprise apps requiring pagination",
                            "Use Query Methods for simple lookups to keep the interface clean",
                            "Use JPQL (@Query) for complex joins to maintain DB independence",
                            "Only use nativeQuery = true for DB-specific optimizations (like JSONB)",
                            "Always return Optional for single-result queries to avoid NPEs",
                            "Utilize Pageable and Slice for high-performance data fetching"
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/java-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Return to Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Use <strong>Derived Query Methods</strong> for simple filters, but switch to <strong>@Query</strong> as soon as your method name becomes unreadable (e.g., <code>findByCategoryAndPriceLessThanAndStatusOrderByCreatedAtDesc</code>).
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
